import { useEffect } from 'react';

import type { UserPlans } from '@cfa/pathway-fe-shared/xp';

import { getStepAvailable, getStepResource } from '@/utils/contentStep';
import {
  getPlanStepNameByLang,
  getPlanStepResourceReferenceName,
} from '@/utils/stepName';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { classifyPlanStepResourceError } from '@/routes/TrainingPlans/resourceError/classifyPlanStepResourceError';
import { collectPlanSteps } from '@/routes/TrainingPlans/resourceError/collectPlanSteps';
import { trackResourceNotFound } from '@/routes/TrainingPlans/resourceError/trackResourceNotFound';

interface UseTrackPlanResourceErrorsParams {
  planData: UserPlans | undefined;
  planId: string | undefined;
  language: string;
  locationNumber: string;
  planType: string | undefined;
  enabled?: boolean;
}

/** Fires deduped resource_not_found events when plan data resolves with bad document steps. */
export const useTrackPlanResourceErrors = ({
  planData,
  planId,
  language,
  locationNumber,
  planType,
  enabled = true,
}: UseTrackPlanResourceErrorsParams) => {
  const track = useAmplitudeTrack();

  useEffect(() => {
    if (!enabled || !planData || !planId || !planType) {
      return;
    }

    const steps = collectPlanSteps(planData.plan?.content);

    for (const step of steps) {
      const error = classifyPlanStepResourceError(step);
      if (!error) {
        continue;
      }

      trackResourceNotFound(track, {
        plan_id: planId,
        step_id: step.id,
        resource_id: getStepResource(step)?.id ?? null,
        resource_name: getPlanStepResourceReferenceName(step, language),
        step_name: getPlanStepNameByLang(step, language),
        plan_type: planType,
        location_number: locationNumber,
        missing_fields: error.missing_fields,
        // Absent `available` means XP did not report the resource as
        // available, which is how classifyPlanStepResourceError reads it too.
        xp_step_available: getStepAvailable(step) ?? false,
      });
    }
  }, [enabled, language, locationNumber, planData, planId, planType, track]);
};
